/**
 * O QUE SE FAZ COM UMA FICHA — e o que fica anotado quando se faz.
 *
 * Regras:
 *  - Pesos e origem só mudam com a ficha ABERTA. Encerrada, reabre-se com
 *    motivo, e a reabertura fica no histórico e aparece no romaneio (o boi que
 *    pulou o curral no último embarque; os bois «garimpados» depois da lida).
 *  - A NEGOCIAÇÃO pode ser corrigida mesmo com a ficha encerrada: cada
 *    correção sobe a versão do romaneio e guarda como ela era antes.
 *  - Cancelar não apaga: a ficha fica no ficheiro, marcada, fora dos totais.
 *    Só se exclui ficha aberta sem nenhum registro.
 */

import { BASES, type Ficha, type Negociacao } from '../dominio/tipos'
import { agora, num } from '../dominio/numeros'
import { descontosDoLote } from '../dominio/contas'
import { lerEstado, mudar, mudarFicha } from './armazem'

export function encerrar(id: string): void {
  mudarFicha(id, (f, e) => {
    f.situacao = 'encerrada'
    f.emitente = { ...e.ajustes.emitente }
    f.historico.push({ em: agora(), oque: 'Encerrada' })
  })
}

export function reabrir(id: string, motivo: string): void {
  mudarFicha(id, (f) => {
    f.situacao = 'aberta'
    f.historico.push({ em: agora(), oque: 'Reaberta' + (motivo ? ': ' + motivo : '') })
  })
}

export function cancelar(id: string, motivo: string): void {
  mudarFicha(id, (f) => {
    f.situacao = 'cancelada'
    f.historico.push({ em: agora(), oque: 'Cancelada: ' + motivo })
  })
}

export function excluir(id: string): boolean {
  const f = lerEstado().fichas.find((x) => x.id === id)
  if (!f || f.situacao !== 'aberta' || f.registros.length) return false
  if (f.revServidor) {
    // já está no banco: lá nada se apaga — vira «excluída» e some das listas
    mudarFicha(id, (x) => { x.situacao = 'excluida'; x.historico.push({ em: agora(), oque: 'Excluída (aberta por engano, sem pesagem)' }) })
  } else {
    mudar((e) => {
      e.fichas = e.fichas.filter((x) => x.id !== id)
      e.pendentes.fichas = e.pendentes.fichas.filter((x) => x !== id)
    })
  }
  return true
}

/** O que mudou na negociação, em português — vai para o histórico e para o romaneio. */
export function diferencas(antes: Negociacao, depois: Negociacao): string[] {
  const d: string[] = []
  const preco = (v: number | null) => (v === null ? 'sem preço' : 'R$ ' + num(v, 2))
  for (const l of depois.lotes) {
    const a = antes.lotes.find((x) => x.id === l.id)
    if (!a) { d.push(`lote novo: ${l.categoria}`); continue }
    const nome = l.categoria
    if (a.categoria !== l.categoria) d.push(`categoria ${a.categoria} → ${l.categoria}`)
    if (a.base !== l.base) d.push(`${nome}: base ${BASES[a.base].nome.toLowerCase()} → ${BASES[l.base].nome.toLowerCase()}`)
    if (a.preco !== l.preco) d.push(`${nome}: preço ${preco(a.preco)} → ${preco(l.preco)}`)
    if (a.rendimento !== l.rendimento && l.base === 'arroba') d.push(`${nome}: rendimento ${num(a.rendimento, 1)}% → ${num(l.rendimento, 1)}%`)
    // 3D: descontos por lote — compara o que VALE (o do lote ou, se não tem, o da ficha),
    // para o lote antigo que só ganhou o número explícito não virar «mudança»
    const da = descontosDoLote(a, antes), dl = descontosDoLote(l, depois)
    if (da.financeiroPct !== dl.financeiroPct) d.push(`${nome}: financeiro ${num(da.financeiroPct, 2)}% → ${num(dl.financeiroPct, 2)}%`)
    if (da.funruralPct !== dl.funruralPct) d.push(`${nome}: funrural ${num(da.funruralPct, 2)}% → ${num(dl.funruralPct, 2)}%`)
  }
  for (const a of antes.lotes) if (!depois.lotes.some((x) => x.id === a.id)) d.push(`lote removido: ${a.categoria}`)
  // os % da ficha só mudam por eles mesmos em ficha antiga; o efeito nos lotes já foi dito acima
  return d
}

export function negociacaoDe(f: Ficha): Negociacao {
  return { lotes: structuredClone(f.lotes), financeiroPct: f.financeiroPct, funruralPct: f.funruralPct }
}
